import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { userAppContext } from '../../../../../context/Context';
import { useEnrichmentState } from '../../../../../hooks/useEnrichmentState';
import { trackEnrichmentAction } from '../../../../../utils/analytics';

import { EVIDENCE_LEGEND, restrictedChemicalColumns, chemicalRestrictedColumns } from '../../constants';

import RenderInfoLogic from '../../../../../components/InfoSidebar/RenderInfoLogic';
import updateParams from '../../../../../utils/updateParams';
import { getAllColumns } from '../../../../../components/Table/columns/index';
import { ColumnVisibilityModels } from '../../../../../components/Table/ColumnVisibilityModels';
import { isRunEnrichmentEnabled } from '../../../../../components/Table/utils/index';

import SharedButton from '../../../../../global/Button/Button';
import { UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert';
import { isSourceEnabledForTab } from '../../helpers/sourceListHelpers';
import { EvidenceParams, evidenceSummaryRequest } from '../../helpers/tableHelpers';
import { deleteFilter } from '../../../../../services/savedFiltersService';

// Hooks
import { useColumnManagement } from './hooks/useColumnManagement';
import { useAlertSystem } from './hooks/useAlertSystem';
import { useSaveOperations } from './hooks/useSaveOperations';
import { useConfigurationLoader } from './hooks/useConfigurationLoader';
import { useDownloadOperations } from './hooks/useDownloadOperations';

// Components
import { SaveSearchModal } from './components/SaveSearchModal';
import { SaveColumnsModal } from './components/SaveColumnsModal';
import { SaveScoreModal } from './components/SaveScoreModal';
import { GridToolbar } from './components/GridToolbar';
import { AcademicRestrictions } from './components/AcademicRestrictions';
import { GridContainer } from './components/GridContainer';
import { RightAsideSidebar } from './components/RightAsideSidebar';
import { useRightSidebarLayout } from '../../context/RightSidebarLayoutContext';
import { useTableStore } from '@/store/tableStore';

// Types
import { ManageGridProps, SearchItem } from './types';

import './EnrichmentSelectionBar.css';
import SharedBadge from '@/global/Badge/Badge';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';

function extractHeaderText(node: any): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node).trim();
  if (Array.isArray(node)) return node.map(extractHeaderText).filter(Boolean).join(' ').trim();
  if (React.isValidElement(node)) {
    return extractHeaderText((node.props as any)?.children);
  }
  return '';
}

function ManageGrid({
  isMapping,
  mappingContent,
  restrictChemical,
  view,
  tab,
  panelTab,
  isEvidence,
  source,
  searchItems,
  setSearchItems,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  dispatch,
  defaultFilterValue,
  sourceWeights = [],
  onOpenSearchItemInfo,
  columnVisibilityModel: controlledColumnVisibilityModel,
  setColumnVisibilityModel: controlledSetColumnVisibilityModel,
  columnOrder: controlledColumnOrder,
  setColumnOrder: controlledSetColumnOrder,
  onColumnStateInit,
  showRightSidebar = true,
  resetRef,
}: ManageGridProps) {
  const sourceCurated = [
    'CURATED',
    'CLINGEN',
    'CLINPGX',
    'CLINVAR',
    'GENCC',
    'MGD_HUMAN',
    'ORPHANET',
    'PSYGENET',
    'RGD_HUMAN',
    'UNIPROT',
    'GWASCAT',
    'PHEWASCAT',
  ];
  const { authorization, isAuthenticated, selectedInfoItem, restrictSources, user } =
    userAppContext() as unknown as UserContext;

  const allColumns = getAllColumns({ isEvidence });
  const downloadColumnLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    allColumns.forEach(col => {
      if (!col?.name) return;
      const label = extractHeaderText(col.header);
      labels[col.name] = label || col.name;
    });
    return labels;
  }, [allColumns]);

  const savedColumsApplied = useTableStore(s => s.savedColumsApplied);

  const [totalResults, setTotalResults] = useState(0);
  const getFilterValueRef = React.useRef<any>(null);

  // Column management: use controlled state from parent when provided, else internal
  const internalColumnManagement = useColumnManagement({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
    restrictChemical,
    searchItems,
  });
  const reportedInitRef = useRef(false);

  // Allow onColumnStateInit to fire again after a tab change
  useEffect(() => {
    reportedInitRef.current = false;
  }, [tab, isEvidence]);

  const columnVisibilityModel =
    controlledColumnVisibilityModel !== undefined
      ? controlledColumnVisibilityModel
      : internalColumnManagement.columnVisibilityModel;
  const setColumnVisibilityModel =
    controlledSetColumnVisibilityModel ?? internalColumnManagement.setColumnVisibilityModel;
  const columnOrder =
    controlledColumnOrder !== undefined ? controlledColumnOrder : internalColumnManagement.columnOrder;
  const setColumnOrder = controlledSetColumnOrder ?? internalColumnManagement.setColumnOrder;
  const hasLoadedFromSavedRef = internalColumnManagement.hasLoadedFromSavedRef;

  const [urlSearchParams] = useSearchParams();
  const urlIdents = urlSearchParams.get('idents') || '';
  const urlView = urlSearchParams.get('view') || '';

  const internalColumnVisibilityModel = internalColumnManagement.columnVisibilityModel;
  const isControlledColumnState = controlledColumnVisibilityModel !== undefined;

  const setColumnVisibilityModelStableRef = useRef(setColumnVisibilityModel);
  setColumnVisibilityModelStableRef.current = setColumnVisibilityModel;
  const setColumnOrderStableRef = useRef(setColumnOrder);
  setColumnOrderStableRef.current = setColumnOrder;
  const columnOrderRef = useRef(columnOrder);
  columnOrderRef.current = columnOrder;

  // Snapshot of the previous internal model to detect per-column diffs.
  const prevInternalModelRef = useRef<Record<string, boolean> | null | undefined>(null);

  // Reset snapshot on tab/evidence-mode changes (controlled model also resets then).
  useEffect(() => {
    prevInternalModelRef.current = null;
  }, [tab, isEvidence]);

  useEffect(() => {
    if (!isControlledColumnState) return;
    if (!internalColumnVisibilityModel) return;

    const prevModel = prevInternalModelRef.current;
    prevInternalModelRef.current = internalColumnVisibilityModel;

    // Skip the very first run after mount/tab-change — onColumnStateInit already set the model.
    if (prevModel === null) return;

    const conditionalColumns = ['diseaseName', 'geneSymbol', 'variantStrID', 'strID'];
    const becameVisible: string[] = [];
    const becameHidden: string[] = [];

    conditionalColumns.forEach(col => {
      const prev = prevModel?.[col];
      const next = internalColumnVisibilityModel[col];
      if (next !== undefined && prev !== next) {
        if (next === true) becameVisible.push(col);
        else becameHidden.push(col);
      }
    });

    if (becameVisible.length === 0 && becameHidden.length === 0) return;

    // Apply only the changed conditional columns; leave all other columns untouched.
    setColumnVisibilityModelStableRef.current(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      [...becameVisible, ...becameHidden].forEach(col => {
        updated[col] = internalColumnVisibilityModel[col];
      });
      return updated;
    });

    // Only remove the columns that actually changed (becameVisible + becameHidden).
    // Conditional columns that were already visible (e.g. geneSymbol) must be preserved
    // in their current position.
    const changedCols = new Set([...becameVisible, ...becameHidden]);
    const preserved = (columnOrderRef.current ?? []).filter(c => !changedCols.has(c));
    // Newly visible columns go first; hidden ones are dropped (not in becameVisible).
    setColumnOrderStableRef.current([...becameVisible, ...preserved]);
  }, [internalColumnVisibilityModel, isControlledColumnState]);

  const resetToDefault = useCallback(() => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(
            view,
            tab,
            isEvidence,
            searchItems,
            urlIdents,
            urlView,
          );
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];
    const defaultModel =
      baseDefaultModel && restrictedList.length > 0
        ? Object.fromEntries(
            Object.entries(baseDefaultModel).filter(([key]) => !restrictedList.includes(key)),
          )
        : baseDefaultModel;
    if (defaultModel) {
      setColumnVisibilityModel(() => defaultModel);
      const defaultColumnOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
      setColumnOrder(defaultColumnOrder);
    }
    hasLoadedFromSavedRef.current = false;
  }, [
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent?.isActive,
    mappingContent?.columVisibilityModelKey,
    restrictChemical,
    searchItems,
    urlIdents,
    urlView,
    setColumnVisibilityModel,
    setColumnOrder,
    hasLoadedFromSavedRef,
  ]);

  useEffect(() => {
    if (savedColumsApplied) {
      showSuccessAlert(`Column configuration "${savedColumsApplied.name}" applied successfully`);
    }
  }, [savedColumsApplied]);

  useEffect(() => {
    if (
      onColumnStateInit &&
      controlledColumnVisibilityModel === undefined &&
      columnVisibilityModel &&
      Object.keys(columnVisibilityModel).length > 0 &&
      !reportedInitRef.current
    ) {
      reportedInitRef.current = true;
      onColumnStateInit(isEvidence, columnVisibilityModel, columnOrder ?? undefined);
    }
  }, [onColumnStateInit, controlledColumnVisibilityModel, isEvidence, columnVisibilityModel, columnOrder]);

  useEffect(() => {
    if (resetRef) {
      resetRef.current = resetToDefault;
    }
  }, [resetRef, resetToDefault]);

  // Alert system
  const {
    showAlert,
    alertType,
    showSuccessAlert,
    showErrorAlert,
    showWarningAlert,
    hideAlert,
    getAlertMessage,
  } = useAlertSystem();

  // Save operations
  const saveOperations = useSaveOperations({
    searchItems,
    view,
    tab,
    source,
    isEvidence,
    totalResults,
    columnVisibilityModel,
    columnOrder,
    sourceWeights,
    getFilterValueRef,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Download operations
  const downloadOperations = useDownloadOperations({
    view,
    source,
    tab,
    searchItems,
    isAuthenticated,
    authorization,
    isEvidence,
    isMapping,
    mappingContent,
    totalResults,
    columnVisibilityModel,
    getFilterValueRef,
    dispatch,
    sourceWeights,
    columnLabels: downloadColumnLabels,
    onWarning: showWarningAlert,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Right aside sidebar (table controls) — context as single source of truth so close works on first click
  const { isSidebarOpen, setSidebarOpen } = useRightSidebarLayout();

  useEffect(() => {
    if (!isSidebarOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isSidebarOpen, setSidebarOpen]);

  // Configuration loader
  useConfigurationLoader({
    setColumnVisibilityModel,
    setColumnOrder,
    hasLoadedFromSavedRef,
    dispatch,
    onSuccess: showSuccessAlert,
    onError: showErrorAlert,
  });

  // Local state
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [areEvidenceSelectedRows, setEvidenceSelectedRows] = useState(false);
  const [evidenceSummParams, setEvidenceSummParams] = useState<EvidenceParams[]>([]);
  const [enrichmentSelectionMode, setEnrichmentSelectionMode] = useState(false);
  const [enrichmentSelectedCount, setEnrichmentSelectedCount] = useState(0);
  const enrichmentGetSelectionRef = useRef<
    | (() => {
        selectedIds: string[];
        rowData: any[];
        getRowId: ((row: any, index: number) => string) | null;
        tab?: string;
      })
    | null
  >(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { setEnrichmentFrom } = useEnrichmentState();

  const getRowIdForEnrichment = (row: any): string => {
    if (tab === 'GENES') {
      return String(
        row.gene_id ??
          row.id ??
          row.geneID ??
          row.geneId ??
          row.geneNcbiID ??
          row.ncbiID ??
          row.gene_ncbi_id ??
          row.geneNcbiIds?.[0] ??
          row.geneSymbol ??
          row.gene_symbol ??
          row.symbol ??
          '',
      );
    }
    if (tab === 'GDA' || tab === 'VDA') {
      return String(
        row.geneSymbol ??
          row.gene_symbol ??
          row.symbol ??
          row.geneNcbiID ??
          row.geneID ??
          row.gene_ncbi_id ??
          row.geneNcbiIds?.[0] ??
          '',
      );
    }
    if (tab === 'VARIANTS') {
      return String(row.strID ?? row.variantStrID ?? row.strId ?? '');
    }
    return '';
  };

  const enrichmentTabs = ['GDA', 'VDA', 'GENES', 'VARIANTS'];
  const canShowEnrichmentSelection = !isEvidence && enrichmentTabs.includes(tab);
  const enableEnrichmentSelection = canShowEnrichmentSelection && enrichmentSelectionMode;

  const enrichmentSelectionModeRef = useRef(enrichmentSelectionMode);
  enrichmentSelectionModeRef.current = enrichmentSelectionMode;
  useLayoutEffect(() => {
    enrichmentSelectionModeRef.current = enrichmentSelectionMode;
    if (!enrichmentSelectionMode) {
      setEnrichmentSelectedCount(0);
    }
  }, [enrichmentSelectionMode]);

  // User permissions
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);
  const isAcademic = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'][0] === 'ROLE_ACADEMIC';
  const isTrial = Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_TRIAL');

  const isChemicalRestricted = isAuthenticated && isAcademic;

  const shouldShowMessage =
    isChemicalRestricted &&
    (tab === 'GDA' || tab === 'VDA') &&
    Object.keys(columnVisibilityModel).some(key => restrictedChemicalColumns.includes(key));

  // Effects
  useEffect(() => {
    const searchAndMappingItems = searchItems
      ? searchItems.concat((mappingContent?.mappingItems as SearchItem[]) || [])
      : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  const handleRemoveSearchItem = (index: number) => {
    if (!searchItems?.length) return;
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const sourceParam = searchParams.get('source');
    const tabParam = searchParams.get('tab');
    const remainingSearchItems = searchItems.filter((_, i) => i !== index);
    if (remainingSearchItems.length === 0) {
      navigate('/');
    } else {
      const newParams = updateParams({
        searchItems: remainingSearchItems as any,
        view: viewParam ?? undefined,
        source: sourceParam ?? undefined,
        tab: tabParam ?? undefined,
      });
      navigate({ pathname: location.pathname, search: newParams });
    }
  };

  // Handlers
  const handleDataFromChild = (data: boolean, params: EvidenceParams[] | any[], selectedCount?: number) => {
    const isEnrichmentMode = enrichmentSelectionModeRef.current || enrichmentSelectionMode;
    if (isEnrichmentMode) {
      const rows = Array.isArray(params) ? params : [];
      const count = selectedCount ?? rows.length;
      setEnrichmentSelectedCount(count);
    } else {
      setEvidenceSelectedRows(data);
      setEvidenceSummParams(params as EvidenceParams[]);
    }
  };

  const handleEvidenceSummClick = async () => {
    const content = await evidenceSummaryRequest(evidenceSummParams, dispatch);
    dispatch({
      type: 'SET_EVIDENCE_MODAL_DATA',
      payload: evidenceSummParams,
    });
    await dispatch({ type: 'SET_EVIDENCE_MODAL_CONTENT', payload: content });
    dispatch({ type: 'SET_SHOW_EVIDENCE_MODAL', payload: true });
  };

  const handleRunEnrichmentFromTable = () => {
    const getSelection = enrichmentGetSelectionRef.current;
    if (!getSelection) return;
    const { selectedIds, rowData: gridRowData, getRowId: gridGetRowId, tab: gridTab } = getSelection();
    if (!selectedIds || selectedIds.length < 2) return;
    const idSet = new Set(selectedIds);
    const getRowIdFn = gridGetRowId ?? null;
    let rowsToUse: any[] =
      gridRowData?.length && getRowIdFn
        ? gridRowData.filter((row: any, index: number) => idSet.has(getRowIdFn(row, index)))
        : [];
    if (rowsToUse.length < 2 && gridRowData?.length) {
      const byIndex = selectedIds
        .map((id: string) => {
          const i = parseInt(id, 10);
          return Number.isNaN(i) ? null : gridRowData[i];
        })
        .filter(Boolean);
      if (byIndex.length >= 2) rowsToUse = byIndex;
    }
    if (rowsToUse.length < 2) return;

    const currentTab = gridTab ?? panelTab ?? tab;
    const isGenes = currentTab === 'GDA' || currentTab === 'GENES';
    const searchType = isGenes ? 'GENES' : 'VARIANTS';
    const selectedFilter = isGenes ? 'geneSymbol' : 'strID';
    const text = isGenes
      ? rowsToUse
          .map((row: any) => String(row.geneSymbol ?? row.symbol ?? row.geneNcbiID ?? row.geneID ?? ''))
          .filter(Boolean)
          .join('\n')
      : rowsToUse
          .map((row: any) => row.strID ?? row.variantStrID ?? '')
          .filter(Boolean)
          .join('\n');

    trackEnrichmentAction('enrichment_analysis_click', 'summary_table');
    const returnTo = location.pathname + location.search;
    const payload = { from: 'summaryTable', searchType, selectedFilter, text, returnTo };
    setEnrichmentFrom(payload);
    navigate('/enrichment', { state: payload });
  };

  const handleToggleEnrichmentSelectionMode = () => {
    setEnrichmentSelectionMode(prev => !prev);
  };

  const handleResetToDefault = () => {
    resetToDefault();
    showSuccessAlert('Columns reset to default');
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      const baseModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

      if (baseModel) {
        const mergedModel = { ...baseModel };
        Object.keys(savedColumn.columnVisibilityModel).forEach(key => {
          if (key in mergedModel) {
            mergedModel[key] = savedColumn.columnVisibilityModel[key];
          }
        });
        setColumnVisibilityModel(() => mergedModel);
      } else {
        setColumnVisibilityModel(() => savedColumn.columnVisibilityModel);
      }
    }

    if (savedColumn.columnOrder) {
      setColumnOrder(savedColumn.columnOrder);
    }

    showSuccessAlert(`Column configuration "${savedColumn.name}" applied successfully`);
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
      showSuccessAlert('Column configuration deleted successfully');
    } catch (error) {
      console.error('Error deleting column configuration:', error);
      showErrorAlert('Error deleting column configuration');
    }
  };

  // Memoized columns
  const columnsFromVisibility = useMemo(() => {
    if (!columnVisibilityModel) return null;

    const originalModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
    if (!originalModel) return null;

    const orderedColumns = [];
    const orderedKeys = Object.keys(columnVisibilityModel);

    orderedKeys.forEach(key => {
      if (columnVisibilityModel[key] === true) {
        const column = allColumns.find(col => col.name === key);
        if (column && (column.name !== 'sourceScore' || sourceWeights.length > 0)) {
          orderedColumns.push(column);
        }
      }
    });

    return orderedColumns;
  }, [columnVisibilityModel, sourceWeights, tab, view, isEvidence, allColumns]);

  const columnsFilteredByShow = allColumns.filter(col => {
    if (typeof col.show === 'function') return col.show({ tab, view, isEvidence, source });
    if (col.show !== undefined) return col.show;
    return true;
  });
  const columns = columnsFromVisibility
    ? columnsFromVisibility.filter(col => columnsFilteredByShow.some(c => c.name === col.name))
    : null;

  // Early return for academic chemical restriction
  const academicChemicalRestriction = (
    <AcademicRestrictions
      isAcademic={isAcademic}
      isAuthenticated={isAuthenticated}
      tab={tab}
      source={source}
      sourceCurated={sourceCurated}
      sourceOpen={false}
    />
  );

  if (isAcademic && isAuthenticated && tab === 'CHEMICALS') {
    return academicChemicalRestriction;
  }

  return (
    <>
      {/* Alert System */}
      {showAlert && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert
            message={getAlertMessage(
              saveOperations.isSavingSearch,
              saveOperations.isSavingColumns,
              false,
              saveOperations.isSavingScore,
            )}
            type={alertType}
            onClose={hideAlert}
          />
        </div>
      )}

      <div>
        {shouldShowMessage && (
          <p className="mt-4 text-pink">
            Some columns are not available in your current academic plan.
            <a
              href="/plans"
              className="text-blue-600 hover:text-blue-800 underline ml-1 font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Upgrade your plan
            </a>
            .
          </p>
        )}
      </div>

      <div>
        {!isAuthenticated && view !== 'ALL' && (
          <p className="mt-4 text-pink">
            As an unregistered user you are viewing only 10 results ordered by score for each query. To view
            more, register for an account. Learn more about our <a href="/plans">plans here</a>.
          </p>
        )}
      </div>

      {/* Download Confirmation Modal */}
      {downloadOperations.showDownloadConfirm && (
        <SharedDialog
          isOpen={downloadOperations.showDownloadConfirm}
          onClose={() => downloadOperations.setShowDownloadConfirm(false)}
          title="Large file download warning"
          description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
          confirmText="Yes"
          cancelText="No"
          hasConfirmBtn
          onConfirm={downloadOperations.confirmLargeDownload}
          data-testid="modal"
        >
          <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
        </SharedDialog>
      )}

      {/* Save Modals */}
      <SaveSearchModal
        isOpen={saveOperations.showSaveSearchModal}
        onClose={() => {
          saveOperations.setShowSaveSearchModal(false);
          saveOperations.setSearchName('');
        }}
        onConfirm={saveOperations.confirmSaveSearch}
        searchName={saveOperations.searchName}
        setSearchName={saveOperations.setSearchName}
        searchDataToSave={saveOperations.searchDataToSave}
        isSaving={saveOperations.isSavingSearch}
      />

      <SaveColumnsModal
        isOpen={saveOperations.showSaveColumnsModal}
        onClose={() => {
          saveOperations.setShowSaveColumnsModal(false);
          saveOperations.setColumnSelectionName('');
        }}
        onConfirm={saveOperations.confirmSaveColumnSelection}
        columnSelectionName={saveOperations.columnSelectionName}
        setColumnSelectionName={saveOperations.setColumnSelectionName}
        columnDataToSave={saveOperations.columnDataToSave}
        isSaving={saveOperations.isSavingColumns}
      />

      <SaveScoreModal
        isOpen={saveOperations.showSaveScoreModal}
        onClose={() => {
          saveOperations.setShowSaveScoreModal(false);
          saveOperations.setScoreName('');
        }}
        onConfirm={saveOperations.confirmSaveScore}
        scoreName={saveOperations.scoreName}
        setScoreName={saveOperations.setScoreName}
        sourceWeights={sourceWeights}
        view={view}
        tab={tab}
        isSaving={saveOperations.isSavingScore}
      />

      {/* Main Content: table area (left info panel is rendered in SearchResults) */}
      <div className="w-full min-w-0">
        <div className="Cabtabla mt-3 border-t pt-3">
          {searchItems && (
            <>
              <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-y-2 gap-x-3">
                <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                  <RenderInfoLogic
                    searchItems={[]}
                    selectedInfoItem={selectedInfoItem}
                    setSearchItems={setSearchItems}
                    isMapping={isMapping}
                    showModalExample={false}
                    setShowModalExample={() => {}}
                    hideSearchItemsList
                  />
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <SharedBadge
                      text={isEvidence ? 'Evidence' : 'Summary'}
                      variant="secondary"
                      className="text-center justify-center h-7"
                      size="md"
                    />
                    <SharedBadge
                      text={source.replace('_', ' ')}
                      label="Source:"
                      variant="primaryLight"
                      className="text-center justify-center h-7"
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end gap-2 flex-shrink-0 w-full lg:w-auto">
                  <GridToolbar
                    sourceWeights={sourceWeights}
                    showSaveDropdown={showSaveDropdown}
                    setShowSaveDropdown={setShowSaveDropdown}
                    onSaveComplete={saveOperations.handleSaveComplete}
                    onSaveColumnSelection={saveOperations.handleSaveColumnSelection}
                    onSaveScore={saveOperations.handleSaveScore}
                    isSaving={saveOperations.isSavingSearch || saveOperations.isSavingColumns}
                    onResetToDefault={handleResetToDefault}
                    isAuthenticated={isAuthenticated}
                    isAcademic={isAcademic}
                    isTrial={isTrial}
                    view={view}
                    tab={tab}
                    isEvidence={isEvidence}
                    isMapping={isMapping}
                    mappingContent={mappingContent}
                    columnVisibilityModel={columnVisibilityModel}
                    columnOrder={columnOrder}
                    getFilterValueRef={getFilterValueRef}
                    searchItems={searchItems}
                    onDownload={downloadOperations.handleDownloadFilesWithFilters}
                    showEnrichmentTrigger={canShowEnrichmentSelection}
                    enrichmentSelectionMode={enrichmentSelectionMode}
                    onToggleEnrichmentSelection={handleToggleEnrichmentSelectionMode}
                  />
                  {academicChemicalRestriction}
                </div>
              </div>
            </>
          )}
        </div>

        {canShowEnrichmentSelection && enrichmentSelectionMode && (
          <div className="enrichment-sticky-bar">
            <div className="enrichment-sticky-bar-content">
              <div className="enrichment-sticky-bar-left">
                <span className="enrichment-selection-count">
                  {enrichmentSelectedCount} item{enrichmentSelectedCount !== 1 ? 's' : ''} selected
                </span>
                {enrichmentSelectedCount > 0 && !isRunEnrichmentEnabled(enrichmentSelectedCount) && (
                  <span className="enrichment-hint">Select at least 2 to run enrichment</span>
                )}
              </div>
              <div className="enrichment-sticky-bar-actions">
                <button
                  type="button"
                  onClick={handleToggleEnrichmentSelectionMode}
                  className="enrichment-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRunEnrichmentFromTable}
                  disabled={!isRunEnrichmentEnabled(enrichmentSelectedCount)}
                  title={
                    !isRunEnrichmentEnabled(enrichmentSelectedCount)
                      ? 'Select at least 2 items to run enrichment'
                      : 'Run enrichment with selected items'
                  }
                  className="enrichment-btn-primary"
                >
                  Run enrichment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Academic restrictions and Grid */}
        {isAcademic && !sourceCurated.includes(source) ? (
          <div className="text-center text-gray-500 p-6">
            <h3>Your free academic plan includes curated sources only.</h3>
            <h5>Select your available sources here.</h5>
          </div>
        ) : (
          <GridContainer
            key={defaultFilterValue ? JSON.stringify(defaultFilterValue) : 'default'}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
            view={view}
            source={source}
            tab={tab}
            panelTab={panelTab}
            searchItems={searchItems}
            isEvidence={isEvidence}
            isMapping={isMapping}
            mappingContent={mappingContent}
            setTotalResults={setTotalResults}
            onData={handleDataFromChild}
            getFilterValueRef={getFilterValueRef}
            defaultFilterValue={defaultFilterValue}
            sourceWeights={sourceWeights}
            isInvalidSource={isInvalidSource}
            enableEnrichmentSelection={enableEnrichmentSelection}
            getRowId={
              canShowEnrichmentSelection
                ? (row: any, index: number) => `${getRowIdForEnrichment(row) || 'row'}-${index}`
                : undefined
            }
            getRowIdForEnrichment={canShowEnrichmentSelection ? getRowIdForEnrichment : undefined}
            enrichmentGetSelectionRef={canShowEnrichmentSelection ? enrichmentGetSelectionRef : undefined}
          />
        )}

        {/* Evidence legend */}
        {isEvidence && (
          <div className="flex justify-between mt-2">
            {EVIDENCE_LEGEND}
            {areEvidenceSelectedRows && SHOW_SUMMARIZE && (
              <div>
                <SharedButton onClick={handleEvidenceSummClick}>Summarize</SharedButton>
              </div>
            )}
          </div>
        )}

        {showRightSidebar && (
          <RightAsideSidebar
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
            source={source}
            sourceWeights={sourceWeights}
            view={view}
            tab={tab}
            isEvidence={isEvidence}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            searchItems={searchItems}
            onOpenSearchItemInfo={onOpenSearchItemInfo}
            onRemoveSearchItem={handleRemoveSearchItem}
            onClearAllSearchItems={() => {
              setSearchItems([]);
              navigate('/');
            }}
            isMapping={isMapping}
            onApplySavedColumns={handleApplySavedColumns}
            onDeleteSavedColumns={handleDeleteSavedColumn}
            mappingContent={mappingContent}
            columnOrder={columnOrder}
            isAcademic={isAcademic}
            onResetToDefault={handleResetToDefault}
          />
        )}
      </div>
    </>
  );
}

export default ManageGrid;
